import { handleAddBeneficiary, handleSaveBeneficiaryStatus } from "../../api/fetchers";
import { SuccessCurrencyConversion } from "asset/icons";
import axios from "axios";
import Loader from "component/loader";
import { DocumentDownload, UserAdd } from "iconsax-reactjs";
import React, { useState } from "react";
import { IoMdClose } from "react-icons/io";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { isWithinOtcBusinessHours } from "utilities/otcBusinessHours";

const TransferSuccessModal = ({
    isOpen,
    onClose,
    onDone,
    onViewRequest,
    transactionId,
    isAlreadyBeneficiary = false,
    isUsd,
    beneficiaryRecipientSnapshot,
    saveBeneficiaryStatusSnapshot,
    transferCurrencySnapshot,
    destinationCurrencySnapshot,
    isOtc = false,
}) => {
    const { currency } = useSelector((state) => state);
    const activeCurrency = String(transferCurrencySnapshot || currency || "").toUpperCase();
    const [isDownloading, setIsDownloading] = useState(false);
    const location = useLocation();
    const isBankRecipient = window.location.pathname.includes("same-currency/bank-recipient");
    const isCryptoFlow = window.location.pathname.includes("crypto-wallet");
    const isCryptoToFiatFlow = location.pathname.includes("fiat-wallet");
    const isCrossBorder = location.pathname.includes("cross-border/bank-recipient");
    const isConversion = location.pathname.includes("convert-currency");
    const destinationCurrency = String(
        destinationCurrencySnapshot || activeCurrency || ""
    ).toUpperCase();
    const usesProcessingCopy = isCrossBorder || (destinationCurrency === "USD" && !isConversion);

    const [isAdding, setIsAdding] = useState(false);
    const [hasBeenAdded, setHasBeenAdded] = useState(false);
    const { bankRecipient } = useSelector((s) => s);
    const isUsdKesDestination = destinationCurrency === "USD" || destinationCurrency === "KES";
    const otcStatusMessage = isWithinOtcBusinessHours()
        ? "You’ll be notified of the status of your request\nwithin 30 minutes."
        : "You’ll be notified of the status of your request\nby the next business day.";

    const cryptoFiat = useSelector((s) => s.cryptoFiatTransfer);
    const { recipient } = useSelector((s) => s);
    const { business } = useSelector((s) => s);
    if (!isOpen) return null;

    const handleDownloadReceipt = async () => {
        try {
            setIsDownloading(true);
            const response = await axios.get(`/v1/wallets/download/${transactionId}`, {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `receipt-${transactionId}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Download failed:", err);
            alert("Failed to download receipt.");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleAddToBeneficiary = async () => {
        if (hasBeenAdded) return;

        setIsAdding(true);
        try {
            if (isUsdKesDestination) {
                const accountNumber = saveBeneficiaryStatusSnapshot?.accountNumber;
                const currencyToSave = saveBeneficiaryStatusSnapshot?.currency || destinationCurrency;

                if (!accountNumber || !currencyToSave) {
                    throw new Error("Missing recipient details");
                }

                await handleSaveBeneficiaryStatus({
                    businessId: business._id,
                    data: {
                        accountNumber,
                        currency: currencyToSave,
                    },
                });

                toast.success("Added to beneficiaries");
                setHasBeenAdded(true);
                return;
            }

            const data = (() => {
                if (isCryptoToFiatFlow) {
                    const r = cryptoFiat?.recipient || {};
                    if (!r.accountIdentifier || !r.institution || !r.accountName) {
                        throw new Error("Missing recipient details");
                    }
                    return {
                        accountNumber: r.accountIdentifier,
                        bankName: r.bankName || "",
                        name: r.accountName,
                        currency: cryptoFiat?.currency,
                        bankCode: r.institution,
                        type: "bankTransfer",
                    };
                }

                if (isBankRecipient && activeCurrency === "NGN") {
                    const recipientForBeneficiary = beneficiaryRecipientSnapshot || bankRecipient;
                    if (
                        !recipientForBeneficiary?.accountNumber ||
                        !recipientForBeneficiary?.bankCode ||
                        !recipientForBeneficiary?.bankName ||
                        !recipientForBeneficiary?.name
                    ) {
                        throw new Error("Missing recipient details");
                    }

                    return {
                        accountNumber: recipientForBeneficiary.accountNumber,
                        bankName: recipientForBeneficiary.bankName,
                        name: recipientForBeneficiary.name,
                        currency: "NGN",
                        bankCode: recipientForBeneficiary.bankCode,
                        type: "bankTransfer",
                    };
                }

                if (isUsd) {
                    return { addBeneficiary: true };
                }

                if (!bankRecipient) throw new Error("No bank recipient selected");
                return {
                    accountNumber: bankRecipient.accountNumber,
                    bankName: bankRecipient.bankName,
                    name: bankRecipient.name,
                    currency,
                    bankCode: bankRecipient.bankCode,
                    type: "bankTransfer",
                };
            })();

            await handleAddBeneficiary({ businessId: business._id, data });

            toast.success("Added to beneficiaries");
            setHasBeenAdded(true);
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message ?? "Could not add beneficiary");
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="relative w-full max-w-xl pt-6 pb-4 bg-white rounded-2xl">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-2xl text-[#101828]"
                >
                    <IoMdClose />
                </button>

                <div className="px-4 text-center">
                    <div className="flex justify-center">
                        <SuccessCurrencyConversion />
                    </div>
                    <h2 className="text-2xl font-semibold text-[#101828]">
                        {isOtc
                            ? "Request submitted"
                            : usesProcessingCopy || isCryptoFlow || isCryptoToFiatFlow
                            ? "Your money is on the way!"
                            : isConversion
                              ? "Conversion successful"
                              : "Transfer successful"}
                    </h2>
                    <p className="text-[#667085] mt-2 whitespace-pre-line">
                        {isOtc
                            ? otcStatusMessage
                            : usesProcessingCopy
                            ? "Your transfer is being processed.\nThe recipient should be credited within 24 hours"
                            : isBankRecipient || isCryptoToFiatFlow
                            ? "The receiving bank will notify the recipient soon."
                            : isCryptoFlow
                              ? "This transaction may take up to 30 minutes due to network congestion. The receiving wallet will be notify the recipient once complete."
                              : isConversion
                                ? "Your wallet balances will be updated shortly."
                                : `${recipient.name ?? "Your wallet"} will be credited instantly.`}
                    </p>
                </div>

                {isOtc ? (
                    <div className="mt-6 flex flex-col items-stretch justify-end gap-3 border-t px-6 pt-4 sm:flex-row sm:items-center">
                        <button
                            type="button"
                            onClick={onViewRequest}
                            className="px-6 py-2 font-semibold text-[#45556C]"
                        >
                            View request
                        </button>
                        <button
                            type="button"
                            onClick={onDone}
                            className="rounded-lg bg-[#6F00FF] px-6 py-2 font-semibold text-white hover:bg-[#5A00D6]"
                        >
                            Back to dashboard
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-between gap-4 px-6 pt-4 mt-6 border-t sm:flex-row">
                        {(isBankRecipient || isCryptoToFiatFlow || isCrossBorder) &&
                            !isAlreadyBeneficiary &&
                            (!isUsdKesDestination ||
                                saveBeneficiaryStatusSnapshot?.accountNumber) && (
                                <button
                                    onClick={handleAddToBeneficiary}
                                    disabled={isAdding || hasBeenAdded}
                                    className={`flex items-center text-[14px] gap-2 font-[350]
                   ${hasBeenAdded ? "text-[#11A811]" : "text-[#161827]"}`}
                                >
                                    {isAdding ? (
                                        <Loader size="sm" />
                                    ) : (
                                        <>
                                            <UserAdd size={20} />
                                            {hasBeenAdded ? "Added" : "Add to beneficiaries"}
                                        </>
                                    )}
                                </button>
                            )}

                        <div
                            className={`flex items-center space-x-2 cursor-pointer ${
                                isDownloading
                                    ? "opacity-60 cursor-not-allowed bg-[#6F00FF] px-4 py-2 rounded-lg"
                                    : ""
                            }`}
                            onClick={!isDownloading ? handleDownloadReceipt : undefined}
                        >
                            {isDownloading ? (
                                <Loader size="sm" />
                            ) : (
                                <>
                                    <DocumentDownload size="20" color="#6F00FF" />
                                    <button className="flex items-center text-[16px] font-[450] gap-2 text-[#101828]">
                                        Download receipt
                                    </button>
                                </>
                            )}
                        </div>

                        <button
                            onClick={onDone}
                            className="bg-[#6F00FF] hover:bg-[#5A00D6] text-white px-6 py-2 rounded-lg font-semibold"
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TransferSuccessModal;
